import api from './axiosConfig';
import {
  PayrollSettingDto,
  SavePayrollSetting,
  PayrollConfigurationDto,
  PayrollGroupDto,
  PayrollTaxConfigurationDto,
  PayslipTemplateDto,
  BankTemplateDto,
  PayrollModuleConfigurationDto,
  PayrollGroupCalculationPreviewDto,
  BankTemplateColumnDto,
  SalaryComponentDto,
  CreateSalaryComponent,
  UpdateSalaryComponent,
  SalaryStructureDto,
  SaveSalaryStructure,
  PayrollGroupAssignmentOptionsResponseDto,
  ApplyPayrollGroupRequest,
  PayrollGroupGenerationResultDto,
  PayrollRunDto,
  PayrollRunDetailDto,
  PayrollRunStatus,
  PayrollAdjustmentType,
  TaxDeclarationDto,
  SaveTaxDeclaration,
  TaxDeclarationStatus,
  PayslipDto,
  PayslipDetailDto,
  PayrollDashboardDto,
} from '../types/payrollTypes';

export const payrollApi = {
  // ─── Dashboard ────────────────────────────────────────────
  getDashboard: async (month?: number, year?: number): Promise<PayrollDashboardDto> => {
    const params: Record<string, number> = {};
    if (month !== undefined) params.month = month;
    if (year !== undefined) params.year = year;
    const response = await api.get<PayrollDashboardDto>('/payroll/dashboard', { params });
    return response.data;
  },

  // ─── Settings ─────────────────────────────────────────────
  getSettings: async (): Promise<PayrollSettingDto | null> => {
    const response = await api.get<PayrollSettingDto>('/payroll/settings');
    return response.data;
  },

  saveSettings: async (data: SavePayrollSetting): Promise<{ id: string }> => {
    const response = await api.post<{ id: string }>('/payroll/settings', data);
    return response.data;
  },

  getConfiguration: async (): Promise<PayrollConfigurationDto> => {
    const response = await api.get<PayrollConfigurationDto>('/payroll/configuration');
    return response.data;
  },

  getGroups: async (): Promise<PayrollGroupDto[]> => {
    const response = await api.get<PayrollGroupDto[]>('/payroll/groups');
    return response.data;
  },

  saveGroups: async (groups: PayrollGroupDto[]): Promise<{ id: string }> => {
    const response = await api.put<{ id: string }>('/payroll/groups', { groups });
    return response.data;
  },

  calculateGroup: async (group: PayrollGroupDto): Promise<PayrollGroupCalculationPreviewDto> => {
    const response = await api.post<PayrollGroupCalculationPreviewDto>('/payroll/groups/calculate-preview', { group });
    return response.data;
  },

  getTaxes: async (): Promise<PayrollTaxConfigurationDto> => {
    const response = await api.get<PayrollTaxConfigurationDto>('/payroll/taxes');
    return response.data;
  },

  saveTaxes: async (taxes: PayrollTaxConfigurationDto): Promise<{ id: string }> => {
    const response = await api.put<{ id: string }>('/payroll/taxes', { taxes });
    return response.data;
  },

  getPayslipTemplates: async (): Promise<PayslipTemplateDto[]> => {
    const response = await api.get<PayslipTemplateDto[]>('/payroll/payslip-templates');
    return response.data;
  },

  savePayslipTemplates: async (templates: PayslipTemplateDto[]): Promise<{ id: string }> => {
    const response = await api.put<{ id: string }>('/payroll/payslip-templates', { templates });
    return response.data;
  },

  getBankTemplate: async (): Promise<BankTemplateDto | null> => {
    const response = await api.get<BankTemplateDto | null>('/payroll/bank-template');
    return response.data;
  },

  getBankTemplateSystemColumns: async (): Promise<BankTemplateColumnDto[]> => {
    const response = await api.get<BankTemplateColumnDto[]>('/payroll/bank-template/system-columns');
    return response.data;
  },

  saveBankTemplate: async (template: BankTemplateDto): Promise<{ id: string }> => {
    const response = await api.put<{ id: string }>('/payroll/bank-template', { template });
    return response.data;
  },

  getModuleConfiguration: async (): Promise<PayrollModuleConfigurationDto> => {
    const response = await api.get<PayrollModuleConfigurationDto>('/payroll/module-configuration');
    return response.data;
  },

  saveModuleConfiguration: async (configuration: PayrollModuleConfigurationDto): Promise<{ id: string }> => {
    const response = await api.put<{ id: string }>('/payroll/module-configuration', { configuration });
    return response.data;
  },

  // ─── Salary Components ────────────────────────────────────
  getSalaryComponents: async (activeOnly?: boolean): Promise<SalaryComponentDto[]> => {
    const params: Record<string, boolean> = {};
    if (activeOnly !== undefined) params.activeOnly = activeOnly;
    const response = await api.get<SalaryComponentDto[]>('/payroll/salary-components', { params });
    return response.data;
  },

  createSalaryComponent: async (data: CreateSalaryComponent): Promise<{ id: string }> => {
    const response = await api.post<{ id: string }>('/payroll/salary-components', data);
    return response.data;
  },

  updateSalaryComponent: async (id: string, data: UpdateSalaryComponent): Promise<void> => {
    await api.put(`/payroll/salary-components/${id}`, data);
  },

  // ─── Salary Structure ─────────────────────────────────────
  getSalaryStructure: async (employeeId: string): Promise<SalaryStructureDto | null> => {
    try {
      const response = await api.get<SalaryStructureDto>(`/payroll/salary-structure/${employeeId}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) return null;
      throw error;
    }
  },

  saveSalaryStructure: async (data: SaveSalaryStructure): Promise<{ id: string }> => {
    const response = await api.post<{ id: string }>('/payroll/salary-structure', data);
    return response.data;
  },

  getGroupAssignmentOptions: async (): Promise<PayrollGroupAssignmentOptionsResponseDto> => {
    const response = await api.get<PayrollGroupAssignmentOptionsResponseDto>('/payroll/group-assignments/options');
    return response.data;
  },

  applyPayrollGroup: async (data: ApplyPayrollGroupRequest): Promise<PayrollGroupGenerationResultDto> => {
    const response = await api.post<PayrollGroupGenerationResultDto>('/payroll/group-assignments/apply', data);
    return response.data;
  },

  removePayrollGroupAssignment: async (employeeId: string): Promise<void> => {
    await api.delete(`/payroll/group-assignments/${employeeId}`);
  },

  // ─── Payroll Runs ─────────────────────────────────────────
  getPayrollRuns: async (year?: number, status?: PayrollRunStatus): Promise<PayrollRunDto[]> => {
    const params: Record<string, number> = {};
    if (year !== undefined) params.year = year;
    if (status !== undefined) params.status = status;
    const response = await api.get<PayrollRunDto[]>('/payroll/runs', { params });
    return response.data;
  },

  getPayrollRunDetail: async (id: string): Promise<PayrollRunDetailDto> => {
    const response = await api.get<PayrollRunDetailDto>(`/payroll/runs/${id}`);
    return response.data;
  },

  createPayrollRun: async (
    month: number,
    year: number,
    departmentId?: string | null,
    payrollGroupId?: string | null,
    periodStart?: string | null,
    periodEnd?: string | null
  ): Promise<{ id: string }> => {
    const response = await api.post<{ id: string }>('/payroll/runs', { month, year, departmentId, payrollGroupId, periodStart, periodEnd });
    return response.data;
  },

  processPayrollRun: async (id: string): Promise<void> => {
    await api.post(`/payroll/runs/${id}/process`);
  },

  lockPayrollRun: async (id: string): Promise<void> => {
    await api.post(`/payroll/runs/${id}/lock`);
  },

  submitPayrollForReview: async (id: string): Promise<void> => {
    await api.post(`/payroll/runs/${id}/submit-review`);
  },

  approvePayrollRun: async (id: string): Promise<void> => {
    await api.post(`/payroll/runs/${id}/approve`);
  },

  markPayrollRunPaid: async (id: string): Promise<void> => {
    await api.post(`/payroll/runs/${id}/mark-paid`);
  },

  sendPayrollRunPayslips: async (id: string): Promise<void> => {
    await api.post(`/payroll/runs/${id}/send-payslips`);
  },

  addPayrollAdjustment: async (
    id: string,
    employeeId: string,
    data: { adjustmentType: PayrollAdjustmentType; componentName: string; amount: number; remarks?: string | null }
  ): Promise<void> => {
    await api.post(`/payroll/runs/${id}/employees/${employeeId}/adjustments`, data);
  },

  recalculatePayrollEmployee: async (id: string, employeeId: string): Promise<void> => {
    await api.post(`/payroll/runs/${id}/employees/${employeeId}/recalculate`);
  },

  recalculatePayrollRun: async (id: string): Promise<void> => {
    await api.post(`/payroll/runs/${id}/recalculate`);
  },

  exportPayrollRun: async (id: string): Promise<Blob> => {
    const response = await api.get(`/payroll/runs/${id}/export`, { responseType: 'blob' });
    return response.data;
  },

  // ─── Tax Declarations ─────────────────────────────────────
  getTaxDeclarations: async (employeeId?: string, financialYear?: string): Promise<TaxDeclarationDto[]> => {
    const params: Record<string, string> = {};
    if (employeeId) params.employeeId = employeeId;
    if (financialYear) params.financialYear = financialYear;
    const response = await api.get<TaxDeclarationDto[]>('/payroll/tax-declarations', { params });
    return response.data;
  },

  saveTaxDeclaration: async (data: SaveTaxDeclaration): Promise<{ id: string }> => {
    const response = await api.post<{ id: string }>('/payroll/tax-declarations', data);
    return response.data;
  },

  updateTaxDeclarationStatus: async (id: string, status: TaxDeclarationStatus): Promise<void> => {
    await api.put(`/payroll/tax-declarations/${id}/status`, { status });
  },

  // ─── Tax Proofs ───────────────────────────────────────────
  uploadTaxProof: async (data: { employeeId: string; financialYear: string; section: string; filePath: string; amount: number }): Promise<{ id: string }> => {
    const response = await api.post<{ id: string }>('/payroll/tax-proofs', data);
    return response.data;
  },

  verifyTaxProofDocument: async (documentId: string, data: { verifiedAmount: number; remarks?: string }): Promise<void> => {
    await api.put(`/payroll/tax-proofs/documents/${documentId}/verify`, data);
  },

  // ─── Payslips ─────────────────────────────────────────────
  getPayslips: async (employeeId?: string, payrollRunId?: string): Promise<PayslipDto[]> => {
    const params: Record<string, string> = {};
    if (employeeId) params.employeeId = employeeId;
    if (payrollRunId) params.payrollRunId = payrollRunId;
    const response = await api.get<PayslipDto[]>('/payroll/payslips', { params });
    return response.data;
  },

  getPayslipDetail: async (id: string): Promise<PayslipDetailDto> => {
    const response = await api.get<PayslipDetailDto>(`/payroll/payslips/${id}/detail`);
    return response.data;
  },

  sendPayslipEmail: async (id: string): Promise<void> => {
    await api.post(`/payroll/payslips/${id}/send-email`);
  },

  // ─── Employee Self-Service ────────────────────────────────
  getMySalary: async (): Promise<SalaryStructureDto | null> => {
    try {
      const response = await api.get<SalaryStructureDto>('/payroll/my-salary');
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) return null;
      throw error;
    }
  },

  getMyPayslips: async (): Promise<PayslipDto[]> => {
    const response = await api.get<PayslipDto[]>('/payroll/my-payslips');
    return response.data;
  },

  getMyPayslipDetail: async (id: string): Promise<PayslipDetailDto> => {
    const response = await api.get<PayslipDetailDto>(`/payroll/my-payslips/${id}/detail`);
    return response.data;
  },

  getMyTaxDeclarations: async (financialYear?: string): Promise<TaxDeclarationDto[]> => {
    const params: Record<string, string> = {};
    if (financialYear) params.financialYear = financialYear;
    const response = await api.get<TaxDeclarationDto[]>('/payroll/my-tax-declarations', { params });
    return response.data;
  },

  saveMyTaxDeclaration: async (data: Omit<SaveTaxDeclaration, 'employeeId'>): Promise<{ id: string }> => {
    const response = await api.post<{ id: string }>('/payroll/my-tax-declarations', data);
    return response.data;
  },
};
